<template>
  <div :class="['letter-card', { cancelled: correspondence.isCancelled }]">
    <!-- Primary row -->
    <div class="d-flex flex-row align-items-center gap-2 p-2 letter-header" @click="expanded = !expanded">
      <i :class="['bi', expanded ? 'bi-chevron-down' : 'bi-chevron-right', 'expand-chevron']" />
      <span class="flex-grow-1 small text-muted fw-semibold">{{ correspondence.label }}</span>
      <StatusBadge v-if="correspondence.isCancelled" variant="disabled" compact pill>Voided</StatusBadge>
      <StatusBadge v-else-if="correspondence.isSent" variant="success" compact pill>Sent</StatusBadge>
      <StatusBadge v-else variant="primary" compact pill>Ready</StatusBadge>
      <spinner-view v-if="correspondence.saving" small />
    </div>

    <!-- Cancel note -->
    <div v-if="labels.note" class="letter-cancel-note">
      {{ labels.note }}
    </div>

    <!-- Expandable detail panel -->
    <BCollapse :show="expanded" disable-auto-scroll-on-show>
      <div class="letter-detail-panel">
        <!-- Referenced listing -->
        <div class="detail-section">
          <div class="d-flex align-items-center gap-2">
            <div class="section-label mb-0">Referenced listing</div>
            <button
              v-if="isEditable"
              type="button"
              class="btn btn-sm btn-link p-0 edit-listing-btn"
              title="Change listing"
              @click="openListingModal"
            >
              <i class="bi bi-pencil" />
            </button>
          </div>

          <div v-if="!correspondence.listingId" class="detail-muted">
            <template v-if="isLegacyLetter">Created before listing tracking was added</template>
            <template v-else>No listing attached</template>
          </div>
          <ListingRow
            v-else-if="referencedListing"
            :listing="referencedListing"
            :city-id="context?.cityId"
            :show-menu="isEditable"
            @retry="onListingRetry"
            @upload="onListingUpload"
            @mark-inactive="onListingMarkInactive"
          />
          <div v-else class="detail-muted">
            Listing not found
          </div>
        </div>

        <!-- Status timeline -->
        <div class="detail-section">
          <div class="section-label">Timeline</div>
          <div class="status-timeline">
            <div v-for="(event, i) in timeline" :key="i" class="timeline-event">
              <div class="timeline-dot" :class="event.variant" />
              <div class="timeline-content">
                <div class="timeline-action">{{ event.action }}</div>
                <div class="timeline-meta">
                  {{ formatDateTime(event.date) }}
                  <template v-if="event.user">&middot; {{ event.user }}</template>
                </div>
                <div v-if="event.note" class="timeline-note">{{ event.note }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BCollapse>

    <!-- Secondary metadata row -->
    <div class="d-flex align-items-center gap-1 small text-muted border-top p-1 ps-2">
      <span>{{ formatDate(labels.date, null, true) }}</span>
      <template v-if="labels.user?.displayName">
        <span>&middot;</span>
        <span>{{ labels.user?.displayName }}</span>
      </template>
      <div class="ms-auto d-flex align-items-center gap-0">
        <template v-if="!correspondence.isCancelled">
          <LetterPreviewButton
            :letters="[correspondence]"
            :context="context"
            small
            @print="$refs.printButton.openPrintDialog()"
          />
          <PrintLetterButton
            ref="printButton"
            :context="context"
            :print-option="correspondence.printOption"
            :label="correspondence.label"
            small
            @evidence-updated="$emit('evidence-updated')"
          />
          <IconButton
            v-if="correspondence.isSent"
            icon="arrow-counterclockwise"
            small
            title="Correct the record"
            @click="confirmMode = CONFIRM_MODES.undo"
          />
          <IconButton
            v-else
            icon="send"
            small
            button
            title="Mark sent"
            @click="confirmMode = CONFIRM_MODES.send"
          />
          <IconButton
            icon="slash-circle"
            small
            title="Void"
            button
            @click="confirmMode = CONFIRM_MODES.void"
          />
        </template>
        <IconButton
          v-else
          icon="trash"
          small
          title="Delete permanently"
          @click="confirmMode = CONFIRM_MODES.purge"
        />
      </div>
    </div>

    <EvidenceGateDialog ref="evidenceGate" />

    <!-- Change listing modal -->
    <ModalView
      v-model="editingListing"
      title="Change referenced listing"
      ok-title="Update"
      :disable-ok="editListingId === correspondence.listingId"
      :busy="savingListing"
      @ok="onListingSave"
    >
      <div class="d-flex flex-column gap-2">
        <RadioCard
          label="None"
          description="No listing attached to this letter"
          :selected="editListingId === null"
          @select="editListingId = null"
        />
        <RadioCard
          v-for="listing in listings"
          :key="listing.id"
          :label="listing.title || listing.url || listing.id"
          :selected="editListingId === listing.id"
          @select="editListingId = listing.id"
        >
          <div class="d-flex align-items-center gap-1 mt-1">
            <span v-if="listing.url" class="listing-url text-truncate">{{ listing.url?.replace(/^https?:\/\//, '') }}</span>
            <StatusBadge v-if="listing.status === 'inactive'" variant="disabled" compact>
              Inactive
            </StatusBadge>
            <StatusBadge v-if="listing.evidence?.status === 'captured'" variant="success" compact>
              <i class="bi bi-check"/> Screenshot captured
            </StatusBadge>
            <StatusBadge v-else-if="listing.evidence?.status === 'manual_upload'" variant="success" compact>
              <i class="bi bi-check"/> Screenshot uploaded
            </StatusBadge>
            <StatusBadge v-else-if="listing.evidence?.status === 'failed'" variant="danger" compact>
              <i class="bi bi-check"/> Screenshot failed
            </StatusBadge>
            <StatusBadge v-else variant="warning" compact>
              <i class="bi bi-slash-circle"/> No screenshot
            </StatusBadge>
          </div>
        </RadioCard>
      </div>
    </ModalView>

    <ModalView
      :model-value="Boolean(confirmMode)"
      @update:model-value="v => confirmMode = v ? confirmMode : null"
      :title="confirmModalConfig?.title"
      :ok-title="confirmModalConfig?.okTitle"
      :ok-variant="confirmModalConfig?.okVariant"
      :disable-ok="confirmModalConfig?.noteRequired && !confirmNote"
      @ok="onConfirm"
    >
      <template v-if="confirmMode === CONFIRM_MODES.send">
        <p>
          Confirm this letter has been printed and mailed to the property owner.
        </p>
      </template>
      <template v-else-if="confirmMode === CONFIRM_MODES.undo">
        <p>
          <span class="fw-semibold"><i class="bi-exclamation-triangle"/> This only corrects the record.</span> If
          physically mailed, reverting does not recall the letter. Use this only to indicate that the letter was marked
          as sent in error.
        </p>
      </template>
      <template v-else-if="confirmMode === CONFIRM_MODES.void">
        <p>
          Please indicate below why the letter is being voided.
        </p>
      </template>
      <template v-else-if="confirmMode === CONFIRM_MODES.purge">
        <p class="alert-danger alert">
          <span class="fw-semibold"><i class="bi-exclamation-triangle"/> This action is permanent!</span> Once deleted,
          you will not be able to recover the letter.
        </p>
      </template>
      <div v-if="!confirmModalConfig?.noNote">
        <label class="form-label">
          Note <span class="text-muted">({{ !confirmModalConfig?.noteRequired ? 'optional' : 'required' }})</span>
        </label>
        <BTextarea small v-model="confirmNote" :placeholder="confirmModalConfig?.placeholder" />
      </div>
    </ModalView>
  </div>
</template>

<script>
import Correspondence from "@/models/Correspondence";
import StatusBadge from "@/components/common/StatusBadge.vue";
import SpinnerView from "@/components/common/SpinnerView.vue";
import PrintLetterButton from "@/components/letters/PrintLetterButton.vue";
import IconButton from "@/components/common/IconButton.vue";
import LetterPreviewButton from "@/components/letters/LetterPreviewButton.vue";
import EvidenceGateDialog from "@/components/letters/EvidenceGateDialog.vue";
import {LetterContext} from "@/models/letterContext";
import ModalView from "@/components/common/ModalView.vue";
import BTextarea from "@/components/common/BTextarea.vue";
import BCollapse from "@/components/common/BCollapse.vue";
import RadioCard from "@/components/common/RadioCard.vue";
import ListingRow from "@/components/letters/ListingRow.vue";
import { postApi } from "@/api";

const CONFIRM_MODES = Object.freeze({
  send: 'send',
  undo: 'undo',
  void: 'void',
  purge: 'purge'
});

export default {
  components: {
    BCollapse,
    BTextarea,
    ModalView,
    RadioCard,
    ListingRow,
    EvidenceGateDialog, LetterPreviewButton, IconButton, PrintLetterButton, SpinnerView, StatusBadge},
  props: {
    correspondence: Correspondence,
    context: LetterContext,
    listings: { type: Array, default: () => [] }
  },
  emits: ['updated', 'evidence-updated', 'listings-changed'],
  data() {
    return {
      expanded: false,
      editingListing: false,
      editListingId: null,
      savingListing: false,
      confirmMode: null,
      confirmNote: '',
      confirming: false
    };
  },
  computed: {
    CONFIRM_MODES() {
      return CONFIRM_MODES;
    },
    referencedListing() {
      if (!this.correspondence.listingId) return null;
      return this.listings.find(l => l.id === this.correspondence.listingId) || null;
    },
    isLegacyLetter() {
      return !this.correspondence.listingId && !this.correspondence.hasListingIdField;
    },
    isEditable() {
      return !this.correspondence.isSent && !this.correspondence.isCancelled;
    },
    timeline() {
      const events = [];
      const c = this.correspondence;

      events.push({
        action: 'Created',
        date: c.dateCreated,
        user: c.createdByUser?.displayName || null,
        variant: 'created'
      });

      if (c.isSent || c.dateSent) {
        events.push({
          action: 'Sent',
          date: c.dateSent,
          user: c.sentByUser?.displayName || null,
          note: c.sendNote,
          variant: 'sent'
        });
      }

      if (c.isCancelled || c.dateCancelled) {
        events.push({
          action: 'Voided',
          date: c.dateCancelled,
          user: c.cancelledByUser?.displayName || null,
          note: c.cancelNote,
          variant: 'voided'
        });
      }

      return events.reverse();
    },
    labels() {
      if (this.correspondence.isCancelled) {
        return {
          date: this.correspondence.dateCancelled,
          user: this.correspondence.createdByUser,
          note: this.correspondence.cancelNote
        };
      } else if (this.correspondence.isSent) {
        return {
          date: this.correspondence.dateSent,
          user: this.correspondence.sentByUser,
          note: this.correspondence.sendNote
        };
      } else {
        return {
          date: this.correspondence.dateCreated,
          user: this.correspondence.createdByUser
        };
      }
    },
    confirmModalConfig() {
      switch (this.confirmMode) {
        case CONFIRM_MODES.send:
          return {
            title: 'Mark as sent',
            okTitle: 'Mark as sent',
            placeholder: 'e.g. Sent via USPS certified mail. Tracking # ...'
          };
        case CONFIRM_MODES.undo:
          return {
            title: 'Correct the record',
            okTitle: 'Revert to Ready',
            placeholder: 'e.g. Did not mean to mark as sent'
          };
        case CONFIRM_MODES.void:
          return {
            title: 'Void',
            okTitle: 'Void',
            placeholder: 'e.g. The letter was created in error',
            noteRequired: true,
            okVariant: 'danger'
          };
        case CONFIRM_MODES.purge:
          return {
            title: 'Delete permanently',
            okTitle: 'Delete',
            okVariant: 'danger',
            noNote: true
          };
        default:
          return null;
      }
    },
    showNoteField() {
      return Boolean(this.mode);
    },
    isEvidenceCaptured() {
      const status = this.context?.evidence?.status;
      return status === 'captured' || status === 'manual_upload';
    }
  },
  watch: {
    confirmMode() {
      this.confirmNote = '';
    }
  },
  methods: {
    openListingModal() {
      this.editListingId = this.correspondence.listingId;
      this.editingListing = true;
    },
    async onListingSave() {
      this.savingListing = true;
      try {
        await this.correspondence.updateListingId(this.editListingId);
        this.editingListing = false;
      } catch (err) {
        console.error('Failed to update listing:', err);
      } finally {
        this.savingListing = false;
      }
    },
    async onListingRetry() {
      if (!this.referencedListing || !this.context?.cityId) return;
      try {
        await postApi(`/v1/cities/${this.context.cityId}/listings/${this.referencedListing.id}/evidence/retry`);
        this.$emit('listings-changed');
      } catch (err) {
        console.error('Evidence retry failed:', err);
      }
    },
    onListingUpload() {
      this.$emit('evidence-updated');
    },
    onListingMarkInactive() {
      this.$emit('listings-changed');
    },
    async onConfirm() {
      this.confirming = true;
      try {
        if (this.confirmMode === CONFIRM_MODES.send) {
          const proceed = await this.$refs.evidenceGate.guard(this.context?.evidence);
          if (!proceed) return;
          await this.correspondence.send(this.confirmNote);
        } else if (this.confirmMode === CONFIRM_MODES.undo) {
          await this.correspondence.unsend(this.confirmNote);
        } else if (this.confirmMode === CONFIRM_MODES.void) {
          await this.correspondence.void(this.confirmNote);
        } else if (this.confirmMode === CONFIRM_MODES.purge) {
          await this.correspondence.delete(this.confirmNote);
        }
        this.confirmMode = '';
      } finally {
        this.confirming = false;
      }
    }
  }
}
</script>

<style scoped>
.letter-header {
  cursor: pointer;
  user-select: none;
}
.letter-header:hover {
  background: var(--bs-light);
  border-radius: 4px 4px 0 0;
}
/* Detail panel */
.letter-detail-panel {
  border-top: 1px solid var(--bs-border-color);
  padding: 12px 16px 12px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--bs-light);
}
.edit-listing-btn {
  font-size: 0.7rem;
  color: var(--bs-secondary-color);
  text-decoration: none;
  line-height: 1;
}
.edit-listing-btn:hover {
  color: var(--bs-body-color);
}
.detail-muted {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
  font-style: italic;
}
.detail-warning {
  font-size: 0.8rem;
  color: var(--bs-warning-text-emphasis);
}

/* Referenced listing */
.evidence-thumb {
  max-width: 180px;
}
.evidence-thumb img {
  width: 100%;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
}

/* Status timeline */
.status-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
}
.timeline-event {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  position: relative;
}
.timeline-event:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 4px;
  top: 18px;
  bottom: -6px;
  width: 1px;
  background: var(--bs-border-color);
}
.timeline-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-top: 4px;
  flex-shrink: 0;
  background: var(--color-secondary-dot);
  border: 2px solid var(--color-secondary-dot);
}
.timeline-dot.sent {
  border-color: var(--color-success-dot);
  background: var(--color-success-dot);
}
.timeline-dot.voided {
  border-color: var(--bs-secondary-color);
  background: var(--bs-secondary-color);
}
.timeline-dot.created {
  border-color: var(--color-primary-dot);
  background: var(--color-primary-dot);
}
.timeline-action {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
}
.timeline-meta {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
}
.timeline-note {
  font-size: 0.75rem;
  color: var(--bs-body-color);
  margin-top: 2px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--bs-border-color);
}
</style>
